import React from "react";
import { USE_LOCAL_FALLBACK } from "../../config/firebaseConfig";
import { useOutletContext } from "react-router-dom";
import { books_getInventory, books_getBundle, books_recordSale } from "../../lib/firestoreAdapters";
import { collection, getDocs, addDoc } from "firebase/firestore";
import { db } from "../../services/firebase";
import { getAllStudents, refreshStudentsFromFirestore } from "../../services/studentsService";

function keyInv(campus, sy) { return `books_inventory_${String(campus).toLowerCase()}_${sy}`; }
function loadInv(campus, sy) { try { const raw = localStorage.getItem(keyInv(campus, sy)); return raw ? JSON.parse(raw) : []; } catch { return []; } }
function saveInv(campus, sy, data) { try { localStorage.setItem(keyInv(campus, sy), JSON.stringify(data)); } catch (e) { void 0; } }
function keyBundles(campus, sy) { return `books_bundles_${String(campus).toLowerCase()}_${sy}`; }
function loadBundles(campus, sy) { try { const raw = localStorage.getItem(keyBundles(campus, sy)); return raw ? JSON.parse(raw) : []; } catch { return []; } }
function keySales(campus, sy) { return `books_sales_${String(campus).toLowerCase()}_${sy}`; }
function loadSales(campus, sy) { try { const raw = localStorage.getItem(keySales(campus, sy)); return raw ? JSON.parse(raw) : []; } catch { return []; } }
function saveSales(campus, sy, data) { try { localStorage.setItem(keySales(campus, sy), JSON.stringify(data)); } catch (e) { void 0; } }

const GRADES = ["Nursery","Kinder","Prep","Grade 1","Grade 2","Grade 3","Grade 4","Grade 5","Grade 6","Grade 7","Grade 8","Grade 9","Grade 10","Grade 11","Grade 12"];

export default function BooksSales() {
  const { campus, schoolYear } = useOutletContext();
  const [bundles, setBundles] = React.useState(() => loadBundles(campus, schoolYear));
  const [inv, setInv] = React.useState(() => loadInv(campus, schoolYear));
  const [grade, setGrade] = React.useState(GRADES[0]);
  const [qty, setQty] = React.useState(1);
  const [date, setDate] = React.useState(() => new Date().toISOString().slice(0,10));
  const [isbn, setIsbn] = React.useState("");
  const [students, setStudents] = React.useState(() => getAllStudents());
  const [studentQuery, setStudentQuery] = React.useState("");
  const [selectedStudent, setSelectedStudent] = React.useState(null);
  const [selected, setSelected] = React.useState({});
  const [orNo, setOrNo] = React.useState("");
  React.useEffect(() => {
    async function fetch() {
      if (USE_LOCAL_FALLBACK) {
        setBundles(loadBundles(campus, schoolYear));
        setInv(loadInv(campus, schoolYear));
      } else {
        const b = await books_getBundle(campus, schoolYear, grade);
        setBundles(b ? [b] : []);
        const invData = await books_getInventory(campus, schoolYear);
        setInv(invData);
      }
    }
    fetch();
  }, [campus, schoolYear, grade]);

  const bundle = React.useMemo(() => bundles.find(b => b.grade === grade && b.schoolYear === schoolYear) || null, [bundles, grade, schoolYear]);
  const price = React.useMemo(() => Number(bundle?.price || 0), [bundle]);
  const total = React.useMemo(() => price * Number(qty || 0), [price, qty]);
  const gradeItems = React.useMemo(() => inv.filter(b => String(b.grade || "") === String(grade)), [inv, grade]);
  const selectedItems = React.useMemo(() => gradeItems.filter(b => selected[b.id]), [gradeItems, selected]);
  const selectedTotal = React.useMemo(() => selectedItems.reduce((s, b) => s + Number(b.price || 0), 0), [selectedItems]);

  React.useEffect(() => {
    (async () => {
      try {
        const list = await refreshStudentsFromFirestore();
        if (Array.isArray(list) && list.length) setStudents(list);
      } catch (e) { void e; }
    })();
  }, []);

  function pickStudent(nameOrId) {
    const s = students.find(st => String(st.id) === String(nameOrId)) ||
      students.find(st => String(st.fullname || st.name).toLowerCase() === String(nameOrId).toLowerCase());
    setSelectedStudent(s || null);
  }

  async function checkout() {
    if (!bundle) { alert("No bundle defined for selected grade."); return; }
    const extra = selectedStudent ? {
      studentId: selectedStudent.id,
      studentName: selectedStudent.fullname || selectedStudent.name || "",
      section: selectedStudent.section || (Array.isArray(selectedStudent.sections) ? selectedStudent.sections[0] : ""),
      orNo: orNo || ""
    } : { orNo: orNo || "" };
    if (USE_LOCAL_FALLBACK) {
      const nextInv = inv.map(b => ({ ...b }));
      for (const it of bundle.items) {
        const idx = nextInv.findIndex(r => r.id === it.id);
        if (idx >= 0) {
          nextInv[idx].sold = Number(nextInv[idx].sold || 0) + Number(qty || 0);
        }
      }
      saveInv(campus, schoolYear, nextInv); setInv(nextInv);
      const sales = loadSales(campus, schoolYear);
      sales.push({
        id: `BS-${Date.now()}`,
        campus, schoolYear, date,
        grade, qty: Number(qty || 0),
        price, total, items: bundle.items, ...extra
      });
      saveSales(campus, schoolYear, sales);
      alert("Sale recorded.");
    } else {
      await books_recordSale(campus, schoolYear, date, grade, qty, bundle, extra);
      // Link items to student coverage
      if (extra.studentId) {
        for (const it of bundle.items) {
          await addDoc(collection(db, "books_student_items"), {
            campus: String(campus).toLowerCase(),
            schoolYear,
            date,
            studentId: extra.studentId,
            studentName: extra.studentName,
            section: extra.section || "",
            grade: grade,
            isbn: it.isbn || "",
            title: it.title,
          });
        }
      }
      const invData = await books_getInventory(campus, schoolYear);
      setInv(invData);
      alert("Sale recorded (Firestore).");
    }
  }

  async function quickSellByISBN(e) {
    e.preventDefault();
    const code = isbn.trim();
    if (!code) return;
    setIsbn("");
    const book = inv.find(b => String(b.isbn || "").replace(/[-\s]/g,"") === code.replace(/[-\s]/g,""));
    if (!book) { alert("Book not found for this ISBN in current campus/year."); return; }
    const q = Number(qty || 1);
    if (USE_LOCAL_FALLBACK) {
      const nextInv = inv.map(b => ({ ...b }));
      const idx = nextInv.findIndex(r => r.id === book.id);
      if (idx >= 0) nextInv[idx].sold = Number(nextInv[idx].sold || 0) + q;
      saveInv(campus, schoolYear, nextInv); setInv(nextInv);
      const sales = loadSales(campus, schoolYear);
      sales.push({ id: `BS-${Date.now()}`, campus, schoolYear, date, grade: book.grade || "-", qty: q, price: Number(book.price || 0), total: Number(book.price || 0) * q, items: [{ id: book.id, title: book.title, price: book.price }] });
      saveSales(campus, schoolYear, sales);
      alert(`Sold ${q} × ${book.title}`);
    } else {
      const pseudoBundle = { items: [{ id: book.id, title: book.title, price: book.price }], price: Number(book.price || 0) };
      await books_recordSale(campus, schoolYear, date, book.grade || "-", q, pseudoBundle);
      const invData = await books_getInventory(campus, schoolYear);
      setInv(invData);
      alert(`Sold ${q} × ${book.title}`);
    }
  }

  return (
    <div>
      {USE_LOCAL_FALLBACK && (
        <div className="alert alert-warning">
          Development mode: using local browser storage. In production this will use Firestore.
        </div>
      )}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Sales — {campus.toUpperCase()} — {schoolYear}</h5>
        <div className="d-flex gap-2">
          <input type="date" className="form-control" value={date} onChange={e => setDate(e.target.value)} />
          <input className="form-control" placeholder="OR #" style={{ width: 140 }} value={orNo} onChange={e => setOrNo(e.target.value)} />
          <select className="form-select" value={grade} onChange={e => setGrade(e.target.value)}>{GRADES.map(g => <option key={g} value={g}>{g}</option>)}</select>
          <input type="number" className="form-control" style={{ width: 100 }} min="1" value={qty} onChange={e => setQty(e.target.value)} />
          <button className="btn btn-success" onClick={checkout} disabled={!bundle}>Checkout ₱{total.toLocaleString()}</button>
        </div>
      </div>
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="fw-bold mb-2">Quick Sell by ISBN</h6>
          <form className="d-flex gap-2" onSubmit={quickSellByISBN}>
            <input className="form-control" style={{ maxWidth: 280 }} placeholder="Scan or type ISBN" value={isbn} onChange={e => setIsbn(e.target.value)} />
            <button className="btn btn-outline-primary">Add</button>
          </form>
          <div className="form-text">Use a USB barcode scanner (keyboard wedge). Matches books in this campus/school year.</div>
        </div>
      </div>
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="fw-bold mb-2">Link to Student (optional)</h6>
          <div className="d-flex gap-2 align-items-center">
            <input className="form-control" list="studentsList" placeholder="Type name or ID" value={studentQuery} onChange={e => { setStudentQuery(e.target.value); }} onBlur={() => pickStudent(studentQuery)} style={{ maxWidth: 360 }} />
            <datalist id="studentsList">
              {students.slice(0,500).map(s => <option key={s.id} value={s.fullname || s.name || s.id} />)}
            </datalist>
            {selectedStudent && <span className="text-muted">Selected: {selectedStudent.fullname || selectedStudent.name} • {selectedStudent.gradeLevel || ""} {selectedStudent.section || ""}</span>}
            {selectedStudent && (
              <button
                className="btn btn-outline-secondary"
                onClick={() => {
                  const url = `/books/print/student-order?campus=${encodeURIComponent(campus)}&sy=${encodeURIComponent(schoolYear)}&sid=${encodeURIComponent(selectedStudent.id)}&grade=${encodeURIComponent(grade)}&date=${encodeURIComponent(date)}&or=${encodeURIComponent(orNo || "")}`;
                  window.open(url, "_blank");
                }}
                title="Open printable order sheet for this student"
              >
                Print Order Sheet
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="card">
        <div className="card-body">
          {!bundle ? (
            <div className="text-muted">No bundle defined for {grade}. Create one in the Bundles tab.</div>
          ) : (
            <>
              <h6 className="fw-bold">{grade} Bundle — ₱{Number(bundle.price || 0).toLocaleString()}</h6>
              <ul className="mb-0">
                {bundle.items.map(it => <li key={it.id}>{it.title} — ₱{Number(it.price || 0).toLocaleString()}</li>)}
              </ul>
              <hr />
              <h6 className="fw-bold">Custom Selection</h6>
              <div className="small text-muted mb-2">Pick only the books the student needs this time.</div>
              <div className="table-responsive">
                <table className="table table-sm table-hover">
                  <thead className="table-light">
                    <tr><th><input type="checkbox" onChange={e => setSelected(Object.fromEntries(gradeItems.map(b => [b.id, e.target.checked])))} /></th><th>Title</th><th>ISBN</th><th>Publisher</th><th>Price</th></tr>
                  </thead>
                  <tbody>
                    {gradeItems.map(b => (
                      <tr key={b.id}>
                        <td><input type="checkbox" checked={!!selected[b.id]} onChange={e => setSelected(prev => ({ ...prev, [b.id]: e.target.checked }))} /></td>
                        <td>{b.title}</td><td>{b.isbn || "-"}</td><td>{b.publisher || "-"}</td><td>₱{Number(b.price || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <div className="text-muted">{selectedItems.length} item(s) selected</div>
                <button
                  className="btn btn-outline-success"
                  onClick={async () => {
                    if (selectedItems.length === 0) { alert("Select at least one title."); return; }
                    const items = selectedItems.map(b => ({ id: b.id, isbn: b.isbn || "", grade: b.grade || grade, title: b.title, price: Number(b.price || 0), qty: 1 }));
                    const pseudoBundle = { items, price: selectedTotal };
                    const extra = selectedStudent ? {
                      studentId: selectedStudent.id,
                      studentName: selectedStudent.fullname || selectedStudent.name || "",
                      section: selectedStudent.section || (Array.isArray(selectedStudent.sections) ? selectedStudent.sections[0] : ""),
                      orNo: orNo || ""
                    } : { orNo: orNo || "" };
                    if (USE_LOCAL_FALLBACK) {
                      // local inventory adjust
                      const nextInv = inv.map(b => ({ ...b }));
                      for (const it of items) {
                        const idx = nextInv.findIndex(r => r.id === it.id);
                        if (idx >= 0) nextInv[idx].sold = Number(nextInv[idx].sold || 0) + 1;
                      }
                      setInv(nextInv); saveInv(campus, schoolYear, nextInv);
                      const sales = loadSales(campus, schoolYear);
                      sales.push({ id: `BS-${Date.now()}`, campus, schoolYear, date, grade, qty: 1, price: selectedTotal, total: selectedTotal, items, ...extra });
                      saveSales(campus, schoolYear, sales);
                    } else {
                      await books_recordSale(campus, schoolYear, date, grade, 1, pseudoBundle, extra);
                      if (extra.studentId) {
                        for (const it of items) {
                          await addDoc(collection(db, "books_student_items"), {
                            campus: String(campus).toLowerCase(), schoolYear, date,
                            studentId: extra.studentId, studentName: extra.studentName, section: extra.section || "",
                            grade, isbn: it.isbn || "", title: it.title
                          });
                        }
                      }
                      const invData = await books_getInventory(campus, schoolYear);
                      setInv(invData);
                    }
                    alert("Custom sale recorded.");
                    setSelected({});
                  }}
                >
                  Checkout Selected — ₱{selectedTotal.toLocaleString()}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
